import { ExtensionsSettings } from 'src/constants';

/**
 * Computes which extension config is the effective root for each AppID group
 * in a portal's extensionsSettings.
 *
 * Rules:
 *   - If exactly one ExtensionConfig has a given appId, that one is the root
 *     regardless of its isRoot flag (implicit-root fallback that lets pre-existing
 *     single-instance data work without a backfill).
 *   - If multiple ExtensionConfigs share an appId and exactly one has isRoot=true,
 *     that one wins.
 *   - Otherwise (none flagged or more than one flagged), the resolver falls back
 *     to the lexicographically-first extension config ID for determinism.
 *
 * Entries with an empty/missing appId are excluded from the result.
 *
 * Returns a map keyed by extension config ID pointing at the root extension
 * config ID for that AppID group.
 */
export function resolveRootModuleIds(
  extensionsSettings: ExtensionsSettings | undefined | null,
): Record<string, string> {
  if (!extensionsSettings) {
    return {};
  }

  const idsByAppId = new Map<string, string[]>();
  for (const [id, ext] of Object.entries(extensionsSettings)) {
    const appId = ext?.appId;
    if (!appId) continue;
    const existing = idsByAppId.get(appId);
    if (existing) {
      existing.push(id);
    } else {
      idsByAppId.set(appId, [id]);
    }
  }

  const result: Record<string, string> = {};
  for (const ids of idsByAppId.values()) {
    const root = pickRoot(ids, extensionsSettings);
    for (const id of ids) {
      result[id] = root;
    }
  }
  return result;
}

function pickRoot(ids: string[], extensions: ExtensionsSettings): string {
  if (ids.length === 1) {
    return ids.at(0)!;
  }

  const flagged = ids.filter((id) => extensions[id]?.isRoot);
  if (flagged.length === 1) {
    return flagged.at(0)!;
  }

  return [...ids].sort().at(0)!;
}

/**
 * Returns whether a new ExtensionConfig being saved for `appId` should be
 * marked as the root variant. A new entry becomes the root when no existing
 * ExtensionConfig in `extensionsSettings` already references the same AppID;
 * otherwise it is a non-root variant.
 *
 * Empty/missing AppID always returns false — embed/link extensions without an
 * AppID don't participate in multi-instance grouping.
 */
export function shouldNewExtensionBeRoot(
  extensionsSettings: ExtensionsSettings | undefined | null,
  appId: string | undefined | null,
): boolean {
  if (!appId) return false;
  const rootIDs = resolveRootModuleIds(extensionsSettings);
  return !Object.values(rootIDs).some(
    (rootId) => extensionsSettings?.[rootId]?.appId === appId,
  );
}
